/**
 * Os três estados que toda lista tem: carregando, vazio, e com conteúdo.
 *
 * Estavam escritos à mão em 78 lugares — 56 `<Spinner />` soltos e 22 frases de vazio —, e por isso
 * cada tela dizia o vazio à sua maneira: umas com "Nenhum … ainda.", outras com "Sem …", outras com
 * nada, deixando um branco que parece defeito. O manual §9 é explícito: *"telas vazias são um
 * convite à ação, não um vazio melancólico"*, e uma regra de voz só se cumpre se houver um lugar
 * onde ela é escrita uma vez.
 *
 * `acao` é o que torna o vazio um convite: quando existe um próximo passo óbvio (importar, criar,
 * buscar), ele fica ali, e não numa barra distante.
 */
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';

export function Estado({
  carregando,
  vazio,
  titulo,
  descricao,
  icone: Icone,
  acao,
  children,
  className,
}: {
  carregando?: boolean;
  /** `true` = não há o que mostrar. Quem chama decide (lista vazia, filtro sem resultado…). */
  vazio?: boolean;
  titulo?: string;
  descricao?: string;
  icone?: LucideIcon;
  acao?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  // Carregando VENCE o vazio: uma lista que ainda não chegou não está vazia, e anunciar "nenhum
  // registro" enquanto o pedido está no ar é dar uma resposta que não se tem.
  if (carregando) {
    return (
      <div className={cn('flex items-center justify-center py-10', className)}>
        <Spinner />
      </div>
    );
  }
  if (!vazio) return <>{children}</>;

  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      {Icone && (
        <span className="flex size-11 items-center justify-center rounded-lg bg-surface-2 text-text-tertiary">
          <Icone className="size-5" />
        </span>
      )}
      <div className="flex flex-col gap-1">
        <p className="tipo-corpo font-medium text-text">{titulo ?? 'Nada aqui ainda.'}</p>
        {descricao && <p className="tipo-corpo-pequeno text-text-tertiary">{descricao}</p>}
      </div>
      {acao}
    </div>
  );
}

/**
 * A mesma decisão em forma de função, para o caso mais comum de todos: um painel que já tem o seu
 * cartão e só precisa do miolo.
 *
 * Devolve o nó a mostrar, ou `null` quando há conteúdo — o que permite escrever
 *
 *     {estadoDaLista(carregando, itens.length === 0, 'Nenhum telefone vinculado.') ?? (
 *       …a lista…
 *     )}
 *
 * Esta forma existe por um motivo prático: substituir 22 ternários idênticos por um componente de
 * bloco exigiria reescrever o fechamento de cada um deles à mão, e uma cirurgia dessas em quinze
 * arquivos é onde se introduz o erro que ninguém revisa. Aqui a troca é de uma linha, o fechamento
 * não se move, e o comportamento passa a ser um só.
 */
export function estadoDaLista(
  carregando: boolean,
  vazio: boolean,
  /** Nó, e não `string`: há vazios que dependem do contexto ("preencha marca e modelo" x "ainda
   *  sem histórico"), e forçá-los a texto fixo empobreceria justamente a mensagem que orienta. */
  mensagem: ReactNode,
): ReactNode | null {
  if (carregando) return <Spinner />;
  if (vazio) return <p className="tipo-corpo-pequeno text-text-tertiary">{mensagem}</p>;
  return null;
}
